/**
 * Ersatz fuer die Unicode-Emoji-Flaggen (🇪🇺/🇨🇳) an den beiden Stellen, die eine Lieferanten-Region
 * zeigen: die Umschalt-Chips der Kopfzeile (`App/Kopfzeile.tsx`) und das Faehnchen vor dem
 * Lieferantennamen der Bestellzeile (`Board/Bestellzeile.tsx`, `.order-list-supplier-flagge`).
 *
 * NEUBAU ohne Bestandsvorbild (`index.html` kennt keine Laenderkategorisierung, siehe Kopfkommentar
 * `lib/laender.ts`). Auftrag „Echte SVG-Flaggen statt Emoji fuer EU/China": unter Windows rendert
 * 🇪🇺 auf manchen Systemen nur als Buchstabenkaestchen „EU" statt als Flagge - ein echtes SVG-Bild
 * ist unabhaengig von der Emoji-Schriftart des Betriebssystems.
 *
 * ================= Herkunft der SVG-Dateien =================
 *
 * `src/assets/flags/eu.svg`/`cn.svg` sind woertliche Kopien aus dem MIT-lizenzierten Paket
 * `flag-icons` (https://github.com/lipis/flag-icons, Format `4x3`) - NICHT als Dauerabhaengigkeit:
 * das Paket wurde nur kurz installiert, die zwei Dateien kopiert und das Paket wieder deinstalliert
 * (`package.json` traegt dadurch keine neue Abhaengigkeit). Eine selbst nachgezeichnete Flagge haette
 * das Risiko einer ungenauen Sternanordnung (EU) oder Sternpositionierung (China) getragen.
 *
 * ================= a11y-Entscheidung =================
 *
 * An BEIDEN Aufrufstellen (`Kopfzeile.tsx`, `Bestellzeile.tsx`) stand bisher `aria-hidden="true"` auf
 * dem umschliessenden `<span>`: der Chip traegt selbst schon einen sprechenden `aria-label`
 * ("EU-Lieferanten ausblenden" usw.), die Bestellzeile zeigt den Lieferantennamen direkt daneben als
 * Text. Das Flaggenbild ist an beiden Stellen rein dekorativ redundant zum bereits vorhandenen Text -
 * das `alt`-Attribut bleibt trotzdem gesetzt (sprechend, nicht leer), fuer den Fall, dass ein
 * Aufrufer `aria-hidden` kuenftig weglaesst. `FlaggeProps` erweitert die nativen `<img>`-Props (bis
 * auf `src`/`alt`, die diese Komponente selbst bestimmt) und reicht sie ungeprueft durch - dadurch
 * setzt `Bestellzeile.tsx` unten `title` (die Tooltip-Beschriftung "EU-Lieferant"/"Lieferant in
 * China", woertlich uebernommen aus dem bisherigen `<span title=...>`) und beide Aufrufstellen
 * `aria-hidden="true"`, ohne dass diese Komponente jede einzelne Eigenschaft einzeln benennen muss.
 */
import type { JSX } from "react";
import euFlagge from "../assets/flags/eu.svg";
import cnFlagge from "../assets/flags/cn.svg";

const FLAGGEN: Record<"eu" | "china", { src: string; alt: string }> = {
  eu: { src: euFlagge, alt: "EU-Flagge" },
  china: { src: cnFlagge, alt: "China-Flagge" },
};

export interface FlaggeProps extends Omit<JSX.IntrinsicElements["img"], "src" | "alt" | "className"> {
  /** Passend zum Rueckgabetyp von `supplierRegion()` in `lib/laender.ts` (ohne `"other"` - dafuer
   *  gibt es keine Flagge, siehe die Aufrufstellen). */
  region: "eu" | "china";
  className?: string;
}

export function Flagge({ region, className, ...rest }: FlaggeProps): JSX.Element {
  const { src, alt } = FLAGGEN[region];
  return (
    <img
      src={src}
      alt={alt}
      className={className ? `flagge ${className}` : "flagge"}
      {...rest}
    />
  );
}
